<template>
  <md-card>
    <md-card-content>
        <h5>Total Labourer Count : {{ totalLabourerCount }}</h5>

        <md-table
            :value="tableData"
            :md-sort.sync="currentSort"
            :md-sort-order.sync="currentSortOrder"
            :md-sort-fn="customSort"
            class="paginated-table table-striped table-hover"
        >
          <md-table-toolbar>
            <md-field>
              <label for="pages">Per page</label>
              <md-select v-model="pagination.perPage" name="pages" @change="getScheduleList">
                <md-option
                    v-for="item in pagination.perPageOptions"
                    :key="item"
                    :label="item"
                    :value="item"
                >
                  {{ item }}
                </md-option>
              </md-select>
            </md-field>

            <md-field>
              <md-input
                  type="search"
                  class="mb-3"
                  clearable
                  style="width: 200px"
                  placeholder="Search records"
                  v-model="searchQuery"
                  @input="handleSearch"
              >
              </md-input>
            </md-field>

            <md-field>
              <md-datepicker v-model="searchShiftDate"
                             :key="datepickerKey"
                             md-immediately  />
            </md-field>
          </md-table-toolbar>

          <md-table-empty-state
              md-label="No schedule found"
              :md-description="searchQuery
              ? `No Schedule found for '${searchQuery}'. Try a different term.`
              : 'No schedules found. Try generating schedules.'"
          >
            <md-button
                class="md-primary md-raised"
                @click="$emit('generateSchedules', guidance)"
            >
              Generate Schedules
            </md-button>
          </md-table-empty-state>

          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell md-label="Shift" md-sort-by="shiftName">{{ item.shift ? item.shift.name : '' }}</md-table-cell>
            <md-table-cell md-label="Shift Date" md-sort-by="shift_date" @click="sortBy('shift_date')">{{ item.shift_date }}</md-table-cell>
            <md-table-cell md-label="Day Type" md-sort-by="day_type" @click="sortBy('day_type')">{{ item.day_type }}</md-table-cell>
            <md-table-cell md-label="Shift Start" md-sort-by="start_time">{{ item.start_time }}</md-table-cell>
            <md-table-cell md-label="Shift End" md-sort-by="end_time">{{ item.end_time }}</md-table-cell>
            <md-table-cell md-label="Workplace" md-sort-by="workplaceName" >
              {{ item.workplace ? item.workplace.name : '' }}
            </md-table-cell>
            <md-table-cell md-label="Activity" md-sort-by="activityName">
              {{ item.activity ? item.activity.name : '' }}
            </md-table-cell>
            <md-table-cell md-label="Labourer Count" md-sort-by="labourer_count">{{ item.labourer_count }}</md-table-cell>
            <md-table-cell md-label="Takeon Count" md-sort-by="takeon">{{ item.labourerSchedules ? item.labourerSchedules.length : 0 }}</md-table-cell>
            <md-table-cell md-label="Created At" md-sort-by="created_at">{{ item.created_at }}</md-table-cell>
            <md-table-cell md-label="Actions">
              <md-button
                  class="md-just-icon md-info md-simple"
                  @click="showItem(item)"
              >
                <md-icon>Visibility</md-icon>
              </md-button>
              <md-button
                  class="md-just-icon md-info md-simple"
                  @click="goToReRun(item)"
              >
                <md-icon>autorenew</md-icon>
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
    </md-card-content>

    <md-card-actions md-alignment="space-between">
      <div>
        <p class="card-category">
          Showing {{ from + 1 }} to {{ to }} of {{ pagination.total }} entries
        </p>
      </div>
      <pagination
          class="pagination-no-border pagination-success"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="pagination.total"
          @input="getScheduleList"
      >
      </pagination>
    </md-card-actions>
  </md-card>
</template>

<script>
import { Pagination } from '@/components';
import Swal from 'sweetalert2';

export default {
    name: 'GuidanceScheduleTable',
    components: { Pagination },
    props: {
        guidance: { type: Object, default: () => ({}) },
        type: { type: String, default: 'User' }
    },
    data () {
        return {
            datepickerKey:0,
            tableData: [],
            currentSort: 'shift_date',
            currentSortOrder: 'desc',
            pagination: {
                perPage: 10,
                currentPage: 1,
                perPageOptions: [5, 10, 25, 50],
                total: 0
            },
            searchQuery: '',
            searchShiftDate: '',
            searchTimeout: null
        };
    },
    computed: {
        from () {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        to () {
            let highBound = this.from + this.pagination.perPage;
            return Math.min(highBound, this.pagination.total);
        },
        totalLabourerCount () {
            return this.tableData.reduce((sum, item) => {
                return sum + (item.labourer_count ? Number(item.labourer_count) : 0);
            }, 0);
        }
    },
    created () {
        this.getScheduleList();
    },
    watch: {
        currentSort (newField) {
            this.pagination.currentPage = 1;
            this.getScheduleList();
        },
        currentSortOrder (newOrder) {
            this.pagination.currentPage = 1;
            this.getScheduleList();
        },
        searchShiftDate (val) {
            this.pagination.currentPage = 1;
            this.getScheduleList();
        }
    },

    methods: {
        async getScheduleList () {
            const params = {
                filter: {
                    ...(this.guidance ? { guidance: this.guidance.id } : {}),
                    ...(this.searchQuery ? { queryKey: this.searchQuery } : {}),
                    ...(this.searchShiftDate ? { shift_date: this.searchShiftDate } : {})
                },
                page: {
                    number: this.pagination.currentPage,
                    size: this.pagination.perPage,
                },
                per_page: this.pagination.perPage,
                sort: `${this.currentSortOrder === 'desc' ? '-' : ''}${this.currentSort}`,
                include: 'labourerSchedules,activity,workplace,shift,activity'
            };

            await this.$store.dispatch('schedules/list', params);
            this.tableData = this.$store.getters['schedules/list'];
            this.pagination.total = this.$store.getters['schedules/listTotal'];
        },
        async customSort () {
            return false;
        },

        handleSearch () {
            // debounce requests
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.pagination.currentPage = 1;
                this.getScheduleList();
            }, 400);
        },
        showItem (item) {
            this.$emit('show-item', item);
        },

      goToReRun (item) {
        Swal.fire({
          title: 'Re run Labourer Assignment',
          text: `this will re run labourer assignment. use this to fill missed labourer on takeons`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ok',
          buttonsStyling: true
        }).then(result => {
          if (result.value) {
            this.$emit('reRunAssignment', item);
          }
        });

      },
        handleDelete (item) {
            Swal.fire({
                title: 'Are you sure?',
                text: `Remove Schedule '${item.name}' from ${this.type} ${this.guidance.name}`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonClass: 'md-button md-success btn-fill',
                cancelButtonClass: 'md-button md-danger btn-fill',
                confirmButtonText: 'Yes, delete it!',
                buttonsStyling: false
            }).then(result => {
                if (result.value) {
                    this.$emit('removeSchedule', item);
                }
            });
        },
    }
};
</script>

<style scoped>
</style>
